import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import axios from "axios";
import { Dialog, Button, Dropdown, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import addIcon from "@icons/ic-add-green.svg";
import deleteIcon from "@icons/ic-delete-red.svg";
import AnalyticalNoteView from "../AnalyticalNoteView/AnalyticalNoteView";
import UserInfo from "@utils/UserInfo";
import {
  HeaderWrapper,
  Title,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  CKGridRow,
  FileInput,
  FileInputLabel,
  FileImageWrapper,
  ButtonWrapper,
  CKInputLabel,
  FileName,
  RequiredMessage,
  DocumentRow,
  DeleteSpan,
} from "./AddEditAnalyticalNote.styled";
import "./ckeditor.css";
import closeIcon from "@icons/ic-close-white.svg";

const MAX_TOTAL_UPLOAD_BYTES = 200 * 1024 * 1024; // 200 MB

const AddEditAnalyticalNote = (props) => {
  const {
    t,
    id,
    isEditMode,
    isViewMode,
    title,
    onClose,
    togglePopUp,
    updateRefeshStatus,
    siteId,
  } = props;
  const [isAnalyticalCreating, setIsAnalyticalCreating] = useState(false);
  const [createAnalyticalAPIError, setCreateAnalyticalAPIError] = useState("");
  const analyticalNoteListByIds = useSelector(
    (state) => state.analyticalNotesList.byId
  );
  const selectedAnalyticalNote = analyticalNoteListByIds[id];
  const statusRef = useRef();

  const [descriptionData, setDescriptionData] = useState(
    selectedAnalyticalNote?.description ?? ""
  );

  const [formData, setFormData] = useState({
    status: {
      required: true,
      value: selectedAnalyticalNote?.status ?? "",
      error: "",
    },
    description: {
      required: true,
      value: selectedAnalyticalNote?.description ?? "",
      error: "",
    },
    documents: [],
  });
  const [existingDocuments, setExistingDocuments] = useState([]);

  const onStatusSelect = (selectedStatus) => {
    setFormData({
      ...formData,
      status: {
        value: selectedStatus.value,
        error: "",
        required: true,
      },
    });
    statusRef.current = selectedStatus.value;
  };

  useEffect(() => {
    setFormData({
      ...formData,
      description: {
        value: descriptionData,
        error: "",
        required: true,
      },
    });
  }, [descriptionData]);

  const onFileSelect = (e) => {
    const filesArray = [...formData.documents];
    for (let i = 0; i < e.target.files.length; i++) {
      filesArray.push(e.target.files[i]);
    }
    const totalBytes = filesArray.reduce((sum, f) => sum + (f.size || 0), 0);
    if (totalBytes > MAX_TOTAL_UPLOAD_BYTES) {
      setCreateAnalyticalAPIError(t("GENERAL_FILE_SIZE_ERROR"));
      // reset the input so the same file can be retried after removing others
      e.target.value = "";
      return;
    }
    setCreateAnalyticalAPIError("");
    setFormData({
      ...formData,
      documents: filesArray,
    });
  };

  const deleteFile = (index) => {
    const fileListArr = Array.from(formData.documents);
    fileListArr.splice(index, 1); // here u remove the file

    // recreate file object
    let updatedDocs = [];
    const dT = new DataTransfer();
    if (fileListArr.length > 0) {
      for (let file of fileListArr) {
        dT.items.add(file);
      }
      updatedDocs = dT.files;
    }
    setFormData({
      ...formData,
      documents: updatedDocs,
    });
  };
  const deleteExistingFile = (id) => {
    const documents = existingDocuments.filter((item) => item.id !== id);
    setExistingDocuments(documents);
  };

  const getDocumentList = (files) => {
    let docs = [];
    for (let i = 0; i < files.length; i++) {
      docs[i] = (
        <FileName key={i}>
          <DeleteSpan>
            {" "}
            <Image
              className="deleteImage"
              src={deleteIcon}
              alt="delete"
              onClick={() => deleteFile(i)}
              styles={{ width: "22px", marginTop: "-2px" }}
            />
          </DeleteSpan>{" "}
          {files[i].name}
        </FileName>
      );
    }
    return docs;
  };

  const getExistingDocumentList = (files) => {
    let docs = [];
    for (let i = 0; i < files.length; i++) {
      docs[i] = (
        <FileName key={i}>
          <DeleteSpan>
            {" "}
            <Image
              className="deleteImage"
              src={deleteIcon}
              alt="delete"
              onClick={() => deleteExistingFile(files[i].id)}
              styles={{ width: "22px", marginTop: "-2px" }}
            />
          </DeleteSpan>{" "}
          {files[i].fileName}{" "}
        </FileName>
      );
    }
    return docs;
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.status.value) {
      isFormValid = false;
      data.status.error = t("STATUS_REQUIRED");
    }
    if (data.description.value === "" || data.description.value === "<p></p>") {
      isFormValid = false;
      data.description.error = t("DESCRIPTION_REQUIRED");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    // Get author ID from localStorage (JWT auth)
    const authorId = UserInfo.getUser().id || null;

    const dataModified = new FormData();
    dataModified.append("siteId", siteId);
    dataModified.append("description", formData.description.value);
    dataModified.append("status", formData.status.value);
    dataModified.append("canEdit", false);
    dataModified.append(
      "author",
      JSON.stringify({
        id: authorId,
      })
    );
    dataModified.append("isActive", 1);
    if (formData.documents.length > 0) {
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(
          `document[${i}][type]`,
          JSON.stringify({
            id: 19,
          })
        );
        dataModified.append(
          `document[${i}][filename]`,
          formData.documents[i].name
        );
        dataModified.append(`document[${i}][analyticalNoteId]`, id);
        dataModified.append(`document[${i}][file]`, formData.documents[i]);
      }
    }
    if (existingDocuments.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      existingDocuments.forEach((document) => {
        dataModified.append(`document[${i}][analyticalNoteId]`, id);
        dataModified.append(`document[${i}][filename]`, document.fileName);
        dataModified.append(`document[${i}][id]`, document.id);
        dataModified.append(`document[${i}][file]`, document.fileName);
        i += 1;
      });
    }

    return dataModified;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (!isFormValid) return;

    const totalBytes = Array.from(formData.documents).reduce(
      (sum, f) => sum + (f.size || 0),
      0
    );
    if (totalBytes > MAX_TOTAL_UPLOAD_BYTES) {
      setCreateAnalyticalAPIError(t("GENERAL_FILE_SIZE_ERROR"));
      return;
    }

    {
      const data = getFormData(formData);
      setIsAnalyticalCreating(true);
      setCreateAnalyticalAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post(`/analyticalNotes`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsAnalyticalCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsAnalyticalCreating(false);
            setCreateAnalyticalAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (id && isEditMode) {
        axios
          .post(`/analyticalNotes/${id}`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsAnalyticalCreating(false);
            updateRefeshStatus();
            // onClose();
          })
          .catch((error) => {
            setIsAnalyticalCreating(false);
            setCreateAnalyticalAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };
  useEffect(() => {
    // here not setting description because ck editor creaing issue
    if (selectedAnalyticalNote && isEditMode) {
      setFormData({
        ...formData,
        status: {
          value: parseInt(selectedAnalyticalNote.status, 10),
          error: "",
          required: true,
        },
      });
      setExistingDocuments(selectedAnalyticalNote.documents);
    }
  }, [selectedAnalyticalNote, isEditMode]);

  const onEdit = () => {
    togglePopUp();
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>{title}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Analytical Note price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        {isViewMode && (
          <AnalyticalNoteView analyticalNoteInfo={selectedAnalyticalNote} />
        )}
        {!isViewMode && (
          <FormBody>
            <OptionsWrapper>
              <FlexRow>
                <SectionContainer>
                  <div>
                    <GridRow>
                      <Dropdown
                        label={t("STATUS")}
                        name="status"
                        value={formData.status.value}
                        defaultOption={t("STATUS")}
                        options={[
                          {
                            label: t("DRAFT"),
                            value: 1,
                          },
                          {
                            label: t("IN_PROGRESS"),
                            value: 2,
                          },
                          {
                            label: t("COMPLETED"),
                            value: 3,
                          },
                        ]}
                        labelStyles={{ fontWeight: "bold" }}
                        containerStyles={{ marginBottom: "1.32rem" }}
                        inputStyles={{ border: "1px solid #474747" }}
                        required
                        onChange={onStatusSelect}
                        error={formData.status.error}
                      />
                    </GridRow>
                    <CKGridRow>
                      <div>
                        <CKInputLabel htmlFor="description">
                          {t("DESCRIPTION")}
                        </CKInputLabel>
                        <CKEditorText
                          descriptionData={descriptionData}
                          setDescriptionData={setDescriptionData}
                        />
                        {formData.description.error && (
                          <RequiredMessage>
                            {formData.description.error}
                          </RequiredMessage>
                        )}
                      </div>
                    </CKGridRow>
                    <div style={{ marginTop: "20px" }}>
                      <FileInputLabel>{t("DOCUMENTS")}</FileInputLabel>

                      <FileImageWrapper htmlFor="document">
                        <Image
                          src={addIcon}
                          alt="upload Analytical note."
                          styles={{ width: "auto" }}
                        />
                      </FileImageWrapper>
                      <FileInput
                        type="file"
                        id="document"
                        multiple
                        onChange={onFileSelect}
                      />
                    </div>
                    {isEditMode && (
                      <DocumentRow>
                        {existingDocuments.length > 0 &&
                          getExistingDocumentList(existingDocuments)}
                        {formData.documents.length > 0
                          ? getDocumentList(formData.documents)
                          : ""}
                      </DocumentRow>
                    )}
                    {!isEditMode && (
                      <DocumentRow>
                        {" "}
                        {getDocumentList(formData.documents)}{" "}
                      </DocumentRow>
                    )}
                  </div>
                </SectionContainer>
              </FlexRow>
            </OptionsWrapper>
          </FormBody>
        )}
        <ButtonWrapper>
          {createAnalyticalAPIError && <div>{createAnalyticalAPIError}</div>}
          {isViewMode && selectedAnalyticalNote?.canEdit === 1 && (
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                marginRight: "0.32rem",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onEdit}
            >
              {t("EDIT")}
            </Button>
          )}
          {!isViewMode && (
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                marginRight: "0.32rem",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onSubmit}
            >
              <>
                {isAnalyticalCreating && <ClipLoader size={15} color="#fff" />}
                {!isAnalyticalCreating && t("SUBMIT")}
              </>
            </Button>
          )}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditAnalyticalNote.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  isViewMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditAnalyticalNote.defaultProps = {
  id: null,
  isEditMode: false,
  isViewMode: false,
};
export default withTranslation()(AddEditAnalyticalNote);
